import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription, finalize } from 'rxjs';
import { Application, Job, Student } from '../../../models/college-dashboard.model';
import { CollegeDashboardService } from '../../../services/college-dashboard.service';
import { CollegePortalService } from '../../../../college-workspace/services/college-portal.service';
import {
  StudentApplicationRow,
  StudentDetail,
} from '../../../../college-workspace/models/college-portal.models';

@Component({
  selector: 'app-student-profile-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './student-profile-modal.component.html',
  styleUrl: './student-profile-modal.component.scss'
})
export class StudentProfileModalComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Real, API-backed flow (used by the Students directory page). When set,
   * this takes over and `student` (below) is ignored.
   */
  @Input() studentUserId?: string;

  /**
   * Legacy mock-data flow, still used by Overview/Readiness/Attention widgets
   * that haven't been migrated off CollegeDashboardService's in-memory data.
   * Kept working as-is so this shared modal doesn't break those pages.
   */
  @Input() student?: Student;

  @Output() close = new EventEmitter<void>();
  @Output() viewReadiness = new EventEmitter<Student>();

  activeTab: 'overview' | 'applications' = 'overview';

  // ── Real flow state ──────────────────────────────────────────────────
  studentDetail: StudentDetail | null = null;
  isLoadingStudent = false;

  applications: StudentApplicationRow[] = [];
  isLoadingApplications = false;

  private subscription = new Subscription();

  constructor(
    private collegeService: CollegePortalService,
    private dashboardService: CollegeDashboardService,
    private toastr: ToastrService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.studentUserId) {
      this.loadStudent();
      this.loadApplications();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentUserId'] && !changes['studentUserId'].firstChange && this.studentUserId) {
      this.loadStudent();
      this.loadApplications();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // ── Real flow ─────────────────────────────────────────────────────────

  get initials(): string {
    if (!this.studentDetail) return '';
    return `${this.studentDetail.first_name?.[0] ?? ''}${this.studentDetail.last_name?.[0] ?? ''}`.toUpperCase();
  }

  get statusClass(): string {
    return this.studentDetail?.is_active ? 'success' : 'neutral';
  }

  loadStudent(): void {
    this.isLoadingStudent = true;
    this.subscription.add(
      this.collegeService.getStudentDetail(this.studentUserId!).pipe(
        finalize(() => { this.isLoadingStudent = false; })
      ).subscribe({
        next: (res) => {
          if (res.Success) {
            this.studentDetail = res.Data;
          } else {
            this.toastr.error(res.Message || 'Failed to load student profile.');
          }
        },
        error: () => this.toastr.error('Failed to load student profile.')
      })
    );
  }

  loadApplications(): void {
    this.isLoadingApplications = true;
    this.subscription.add(
      this.collegeService.getStudentApplications(this.studentUserId!).pipe(
        finalize(() => { this.isLoadingApplications = false; })
      ).subscribe({
        next: (res) => {
          if (res.Success && res.Data) {
            this.applications = res.Data.items;
          } else {
            this.toastr.error(res.Message || 'Failed to load job applications.');
          }
        },
        error: () => this.toastr.error('Failed to load job applications.')
      })
    );
  }

  progressLabel(progress: { status: string; score?: number }): string {
    if (progress.status === 'completed') {
      return progress.score !== undefined && progress.score !== null ? `${progress.score}` : 'Completed';
    }
    if (progress.status === 'pending') return 'In Progress';
    return 'Not Started';
  }

  /**
   * Real platform application statuses are lowercase (CandidateStatus enum,
   * e.g. "rejected"/"hired"/"selected"); manually-logged ones are
   * capitalized (ManualApplicationStatus, e.g. "Rejected"/"Offered").
   * Compared case-insensitively so both sources get the right pill color.
   */
  applicationStatusClass(status: string): string {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'rejected') return 'warning';
    if (['offered', 'hired', 'selected'].includes(normalized)) return 'success';
    return 'neutral';
  }

  viewAssessmentReport(app: StudentApplicationRow): void {
    if (!app.assessment.attempt_id) return;
    this.router.navigate(['/assesment-report', app.assessment.attempt_id]);
  }

  viewInterviewReport(app: StudentApplicationRow): void {
    if (!app.interview.session_id || !app.interview.report_token) {
      this.toastr.error('Interview report is not available yet.');
      return;
    }
    this.router.navigate(['/ai-performance-report', app.application_id, app.interview.session_id, app.interview.report_token]);
  }

  // ── Legacy mock flow (Overview / Readiness / Attention) ─────────────────

  get legacyStudentApplications(): Application[] {
    return this.dashboardService.getApplicationsForStudent(this.student!.id);
  }

  get legacyJobs(): Job[] {
    return this.dashboardService.getJobs();
  }

  getJob(jobId: string): Job | undefined {
    return this.legacyJobs.find((j) => j.id === jobId);
  }

  get legacyReadinessLabel(): string {
    // Matches readiness-profile-modal.component.ts's wording — clicking this
    // pill navigates there (onReadinessClick), so the label must agree.
    if (this.student!.score >= 80) return 'Ready';
    if (this.student!.score >= 60) return 'Developing';
    return 'At Risk';
  }

  get legacyReadinessClass(): string {
    if (this.student!.score >= 80) return 'ready';
    if (this.student!.score >= 60) return 'developing';
    return 'needs';
  }

  get legacyStatusClass(): string {
    if (this.student!.placementStatus === 'Placed') return 'success';
    if (this.student!.placementStatus === 'Not Eligible') return 'warning';
    return 'neutral';
  }

  get legacyInitials(): string {
    return this.student!.name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  onReadinessClick(): void {
    this.viewReadiness.emit(this.student!);
  }
}
